import { useEffect } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateJourneys } from 'api/journeys/hooks';
import { useInvalidateLists } from 'api/lists/hooks';
import { handleError } from 'api/query-client';

import {
  JourneyForm,
  PatchPortalInfoType,
  createFormImage,
  createJourneyForm,
  deleteJourneyForm,
  duplicateJourneyForm,
  loadJourneyForm,
  loadJourneyForms,
  loadPortalsUsingJourneyForm,
  publishJourneyForm,
  unpublishJourneyForm,
  updateJourneyForm,
  updateJourneyFormPortalInfo,
} from './requests';

export const getJourneyFormsKey = (journeyId: string) => ['journey', journeyId, 'forms'];
export const getJourneyFormKey = (journeyId: string, formId: string) => [
  ...getJourneyFormsKey(journeyId),
  formId,
];
export const getJourneyFormPortalsKey = (journeyFormId: string) => [
  'journey-form',
  journeyFormId,
  'portals',
];

export const useInvalidateJourneyForms = () => {
  const client = useQueryClient();
  const invalidateJourneyForms = (journeyId: string) => {
    client.invalidateQueries(getJourneyFormsKey(journeyId));
  };

  const invalidateJourneyForm = (journeyId: string, formId: string) => {
    client.invalidateQueries(getJourneyFormKey(journeyId, formId));
  };

  const refetchJourneyForms = (journeyId: string) => {
    client.refetchQueries(getJourneyFormsKey(journeyId));
  };

  const removeJourneyForm = (journeyId: string, formId: string) => {
    client.removeQueries(getJourneyFormKey(journeyId, formId));
  };

  const invalidateJourneyFormPortals = (journeyFormId: string) => {
    client.invalidateQueries(getJourneyFormPortalsKey(journeyFormId));
  };

  return {
    invalidateJourneyForms,
    invalidateJourneyForm,
    refetchJourneyForms,
    removeJourneyForm,
    invalidateJourneyFormPortals,
  };
};

export const useJourneyFormsList = (journeyId: string) => {
  return useQuery({
    queryKey: getJourneyFormsKey(journeyId),
    queryFn: () => loadJourneyForms(journeyId),
  });
};

export const useJourneyForm = (journeyId: string, formId: string) => {
  const client = useQueryClient();

  useEffect(() => {
    return () => {
      client.removeQueries(getJourneyFormKey(journeyId, formId));
    };
  }, [client, journeyId, formId]);

  return useQuery({
    queryKey: getJourneyFormKey(journeyId, formId),
    queryFn: () => loadJourneyForm(formId),
  });
};

export const useCreateJourneyForm = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyForms } = useInvalidateJourneyForms();
  const { invalidateLists } = useInvalidateLists();

  return useMutation({
    mutationFn: (form: Partial<JourneyForm>) => createJourneyForm(journeyId, form),
    onSuccess: (data) => {
      showSuccess(t('createSuccess', { name: data.name }));
      invalidateLists();
      invalidateJourneyForms(journeyId);
    },
  });
};

export const useUpdateJourneyForm = (journeyFormId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyForms, invalidateJourneyFormPortals } = useInvalidateJourneyForms();
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateLists } = useInvalidateLists();

  return useMutation({
    mutationFn: (form: Partial<JourneyForm>) => updateJourneyForm(journeyFormId, form),
    onSuccess: (data) => {
      showSuccess(t('updateSuccess'));
      invalidateLists();
      invalidateJourney(data.journey);
      invalidateJourneyForms(data.journey);
      invalidateJourneyFormPortals(journeyFormId);
    },
  });
};

export const useDeleteJourneyForm = (journeyForm: JourneyForm) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyForms } = useInvalidateJourneyForms();
  const { removeJourneyForm } = useInvalidateJourneyForms();

  return useMutation({
    mutationFn: () => deleteJourneyForm(journeyForm.shortId),
    onSuccess: () => {
      showSuccess(t('deleteSuccess', { name: journeyForm.name }));
      removeJourneyForm(journeyForm.journey, journeyForm.shortId);
      invalidateJourneyForms(journeyForm.journey);
    },
  });
};

export const usePublishJourneyForm = (journeyForm: JourneyForm) => {
  const { t } = useScopedTranslate(locale);
  const { refetchJourneyForms } = useInvalidateJourneyForms();
  return useMutation({
    mutationFn: () => publishJourneyForm(journeyForm.shortId),
    onSuccess: () => {
      showSuccess(t('publishSuccess', { name: journeyForm.name }));
      refetchJourneyForms(journeyForm.journey);
    },
  });
};

export const useUnpublishJourneyForm = (journeyForm: JourneyForm) => {
  const { t } = useScopedTranslate(locale);
  const { refetchJourneyForms } = useInvalidateJourneyForms();

  return useMutation({
    mutationFn: () => unpublishJourneyForm(journeyForm.shortId),
    onSuccess: () => {
      showSuccess(t('unpublishSuccess', { name: journeyForm.name }));
      refetchJourneyForms(journeyForm.journey);
    },
  });
};

export const useDuplicateJourneyForm = (journeyForm: JourneyForm) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyForms } = useInvalidateJourneyForms();

  return useMutation({
    mutationFn: () => duplicateJourneyForm(journeyForm.shortId),
    onSuccess: () => {
      showSuccess(t('duplicateSuccess', { name: journeyForm.name }));
      invalidateJourneyForms(journeyForm.journey);
    },
  });
};

export const useCreateJourneyFormImage = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: createFormImage,
    onError: () => {
      handleError(t('imageSaveFailed'));
    },
  });
};

export const usePortalsUsingJourneyForm = (journeyFormId: string) => {
  return useQuery({
    queryKey: getJourneyFormPortalsKey(journeyFormId),
    queryFn: () => loadPortalsUsingJourneyForm(journeyFormId),
  });
};

type PortalInfoProps = {
  journeyShortId: string;
  journeyFormId: string;
  onSuccess?: () => void;
};

export const useUpdateJourneyFormPortalInfo = (props: PortalInfoProps) => {
  const { journeyShortId, journeyFormId, onSuccess } = props;
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PatchPortalInfoType) => updateJourneyFormPortalInfo(journeyFormId, data),
    onSuccess: async (data) => {
      showSuccess(t('updateSuccess'));
      queryClient.setQueryData(getJourneyFormKey(journeyShortId, journeyFormId), data);
      setTimeout(() => {
        onSuccess?.();
      });
    },
  });
};

const locale = {
  en: {
    createSuccess: "External form '{name}' added",
    deleteSuccess: "External form '{name}' deleted",
    updateSuccess: 'External form successfully saved',
    publishSuccess: "External form '{name}' is now open for submissions",
    unpublishSuccess: "External form '{name}' is now closed for submissions",
    duplicateSuccess: "External form '{name}' duplicated with the name '{name} (copy)'",
    imageSaveFailed: 'Failed to save image',
  },
};
